// Persistent battlefield decals (roadmap #5): bomb craters and burning crash
// sites on land, oil slicks at sea. They are rendered ON TOP of the pristine
// terrain — the server never mutates the shared heightfield — from a round-lived
// list fed by the welcome handshake and one-shot `scar` events, and wiped on
// roundStart (a refloated hull takes her slick back sooner, remove()). Only
// the crash fires cost anything per frame, and those are
// LOD-gated to the camera's neighbourhood.

import * as THREE from "three";
import { mergeBoxes } from "./planes.js";

const UP = new THREE.Vector3(0, 1, 0);

export class Scars {
  constructor(scene, field, waterY, particles) {
    this.field = field;
    this.waterY = waterY;
    this.particles = particles;
    this.group = new THREE.Group();
    scene.add(this.group);
    this.scars = new Map(); // id -> { kind, x, y, z, r, mesh, fireAt }
    this.time = 0;
  }

  clear() {
    for (const s of this.scars.values()) {
      s.mesh.traverse((o) => o.geometry?.dispose?.());
      this.group.remove(s.mesh);
    }
    this.scars.clear();
  }

  setFromWelcome(list) {
    this.clear();
    for (const s of list ?? []) this.add(s.id, s.k, s.x, s.y, s.z, s.r);
  }

  add(id, kind, x, y, z, r) {
    if (this.scars.has(id) || !(r > 0)) return;
    const mesh = kind === 2 ? this._oil(x, z, r) : kind === 1 ? this._wreck(x, z, r) : this._crater(x, z, r);
    this.group.add(mesh);
    this.scars.set(id, { kind, x, y, z, r, mesh, fireAt: 0 });
  }

  // The one decal that doesn't last the round: the slick an anchored hull
  // bled going down, mopped up when she refloats in it (the refloat event).
  remove(id) {
    const s = this.scars.get(id);
    if (!s) return;
    s.mesh.traverse((o) => o.geometry?.dispose?.());
    this.group.remove(s.mesh);
    this.scars.delete(id);
  }

  // Ground height, floored at the sea so a crater on the shore never sinks.
  _groundY(x, z) {
    return Math.max(this.waterY, this.field.surfaceAt(x, z));
  }

  // A flat patch of radius ~r laid ON the ground at (x, z): tilted to the
  // plane through the ground either side of it, so a scorch on a hillside
  // lies on the slope instead of cutting into it uphill and hanging off it
  // downhill. `lift` keeps it proud of the ground (no z-fight); `yaw` is the
  // parent's heading, which the tilt is expressed under.
  _patch(x, z, r, w, h, color, lift, mat, yaw = 0) {
    const d = r * 0.6;
    const sx = (this._groundY(x + d, z) - this._groundY(x - d, z)) / (2 * d);
    const sz = (this._groundY(x, z + d) - this._groundY(x, z - d)) / (2 * d);
    const patch = new THREE.Mesh(mergeBoxes([{ x: 0, y: 0, z: 0, w, h, d: w, color }]), mat);
    patch.position.set(0, this._groundY(x, z) + lift, 0);
    patch.quaternion.setFromUnitVectors(UP, new THREE.Vector3(-sx, 1, -sz).normalize());
    if (yaw) patch.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(UP, -yaw));
    return patch;
  }

  // A crater: a broad scorched char patch, a scatter of blast marks, and a
  // raised dirt rim — every box laid on the terrain it sits over, 0.15 m proud
  // to avoid z-fighting. Reads as deformation without touching the heightfield.
  _crater(cx, cz, r) {
    const boxes = [];
    const scatter = 10 + Math.round(r);
    for (let i = 0; i < scatter; i++) {
      const a = Math.random() * Math.PI * 2;
      const rr = Math.sqrt(Math.random()) * r;
      const dx = Math.cos(a) * rr, dz = Math.sin(a) * rr;
      const s = 0.5 + Math.random() * r * 0.3;
      boxes.push({ x: dx, y: this._groundY(cx + dx, cz + dz) + 0.18, z: dz, w: s, h: 0.1, d: s, color: i % 3 ? 0x2c2822 : 0x3a352c });
    }
    const rim = 12;
    for (let i = 0; i < rim; i++) {
      const a = (i / rim) * Math.PI * 2 + Math.random() * 0.2;
      const dx = Math.cos(a) * r * 1.05, dz = Math.sin(a) * r * 1.05;
      boxes.push({ x: dx, y: this._groundY(cx + dx, cz + dz) + 0.42, z: dz, w: r * 0.4, h: 0.85, d: r * 0.4, color: i % 2 ? 0x6b5c46 : 0x554836 });
    }
    const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
    const mesh = new THREE.Mesh(mergeBoxes(boxes), mat);
    mesh.add(this._patch(cx, cz, r, r * 1.35, 0.12, 0x1c1813, 0.16, mat)); // the scorched char
    mesh.position.set(cx, 0, cz);
    return mesh;
  }

  // A crash site: a charred fuselage stub and torn wing root on a scorched
  // patch. The looping fire/smoke is emitted in update(), LOD-gated.
  _wreck(cx, cz, r) {
    const gy = this._groundY(cx, cz);
    const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
    const mesh = new THREE.Mesh(mergeBoxes([
      { x: 0, y: gy + 0.45, z: 0, w: 3.2, h: 0.8, d: 1.0, color: 0x141110 },        // fuselage stub
      { x: 1.5, y: gy + 0.6, z: 0, w: 1.0, h: 0.95, d: 0.7, color: 0x1c1813 },       // engine block
      { x: -1.1, y: gy + 0.32, z: 0.5, w: 1.3, h: 0.4, d: 1.7, color: 0x1a1712 },    // torn wing root
    ]), mat);
    const yaw = (cx * 0.13 + cz * 0.19) % (Math.PI * 2); // varied wreck heading
    mesh.add(this._patch(cx, cz, r, r * 1.6, 0.1, 0x241f18, 0.05, mat, yaw)); // scorched ground
    mesh.position.set(cx, 0, cz);
    mesh.rotation.y = yaw;
    mesh.userData.fireY = gy + 0.9;
    return mesh;
  }

  // An oil slick: a dark low-opacity disc on the water with a little drifting
  // flotsam.
  _oil(cx, cz, r) {
    const g = new THREE.Group();
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(r, 30).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x090a0c, transparent: true, opacity: 0.52, depthWrite: false }),
    );
    disc.position.y = this.waterY + 0.1;
    g.add(disc);
    const boxes = [];
    const n = 5 + Math.round(r / 8);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, rr = Math.random() * r * 0.8;
      const s = 0.4 + Math.random() * 0.8;
      boxes.push({ x: Math.cos(a) * rr, y: this.waterY + 0.22, z: Math.sin(a) * rr, w: s, h: 0.25, d: s, color: i % 2 ? 0x2a2d33 : 0x161513 });
    }
    g.add(new THREE.Mesh(mergeBoxes(boxes), new THREE.MeshLambertMaterial({ vertexColors: true })));
    g.position.set(cx, 0, cz);
    return g;
  }

  update(dt, cameraPos) {
    this.time += dt;
    for (const s of this.scars.values()) {
      if (s.kind !== 1) continue; // only crash fires burn
      const dx = s.x - cameraPos.x, dz = s.z - cameraPos.z;
      if (dx * dx + dz * dz > 1500 * 1500) continue; // LOD: within 1.5 km
      s.fireAt -= dt;
      if (s.fireAt <= 0) {
        s.fireAt = 0.5; // ~2 Hz
        const fy = s.mesh.userData.fireY ?? s.y;
        this.particles.fire(new THREE.Vector3(s.x + (Math.random() - 0.5) * 2, fy, s.z + (Math.random() - 0.5) * 2));
        this.particles.smoke(new THREE.Vector3(s.x + (Math.random() - 0.5) * 2, fy + 1, s.z + (Math.random() - 0.5) * 2));
      }
    }
  }
}
